import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import styles from "./CustomSunburstChart.module.scss";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.error}>
          Something went wrong rendering the chart.
        </div>
      );
    }
    return this.props.children;
  }
}

const CustomSunburstChart = ({
  data,
  width = 800,
  height = 800,
  theme = "light",
  setShowDeepDive,
  setSelectedTopic,
}) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const tooltipRef = useRef();
  const deepDiveRef = useRef();
  // Add ref to track timeout
  const hideTimeoutRef = useRef(null);

  const handleDeepDive = (nodeData) => {
    console.log("Deep Dive Data:", nodeData);
    if (nodeData.id > 0) {
      setSelectedTopic(nodeData);
      setShowDeepDive(true);
    }
  };

  const closeDeepDive = () => {
    setShowDeepDive(false);
    setSelectedTopic(null);
  };

  useEffect(() => {
    if (!data || !svgRef.current || !tooltipRef.current) return;

    // Clear
    const svgEl = svgRef.current;
    d3.select(svgEl)
      .selectAll("*")
      .remove();

    // Layout
    const margin = 20;
    const radius = Math.min(width, height) / 2.2 - margin;

    // Theme
    const bg = theme === "dark" ? "#1a1a1a" : "#ffffff";
    const textClr = theme === "dark" ? "#ecf0f1" : "#2c3e50";
    const strokeClr = theme === "dark" ? "#5d6d7e" : "#bdc3c7";

    // Enhanced Tooltip with fixed positioning
    const tooltip = d3
      .select(tooltipRef.current)
      .style("position", "fixed")
      .style("pointer-events", "auto")
      .style("opacity", 0)
      .style(
        "background",
        theme === "dark" ? "rgba(44,62,80,0.98)" : "rgba(255,255,255,0.98)"
      )
      .style("color", textClr)
      .style("border", `1px solid ${strokeClr}`)
      .style("padding", "12px 16px")
      .style("border-radius", "8px")
      .style("font-size", "13px")
      .style("white-space", "pre-line")
      .style("z-index", 1000)
      .style("max-width", "280px")
      .style("box-shadow", "0 4px 20px rgba(0,0,0,0.15)")
      .style("backdrop-filter", "blur(10px)");

    // Add helper functions for tooltip management
    const showTooltip = () => {
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      tooltip.style("opacity", 1);
    };

    const hideTooltip = (immediate = false) => {
      if (immediate) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        tooltip.style("opacity", 0);
      } else {
        // Clear existing timeout
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        // Set new timeout
        hideTimeoutRef.current = setTimeout(() => {
          tooltip.style("opacity", 0);
          hideTimeoutRef.current = null;
        }, 300); // Increased delay to 300ms
      }
    };

    // Preprocess data to give minimum values to zero-value items
    function preprocessData(node) {
      const minValue = 1.0;

      if (node.children) {
        // Process children first
        node.children.forEach(preprocessData);

        // For parent nodes, use sum of children but ensure minimum
        const childSum = node.children.reduce(
          (sum, child) => sum + (child.value || 0),
          0
        );
        node.value = Math.max(childSum, minValue);
      } else {
        // For leaf nodes, ensure minimum value
        node.value = Math.max(node.value || 0, minValue);
      }

      return node;
    }

    // Enhanced sorting function to prioritize nodes with more children
    function sortNodesByChildren(a, b) {
      // First, sort by number of children (descending)
      const aChildCount = (a.children && a.children.length) || 0;
      const bChildCount = (b.children && b.children.length) || 0;

      if (aChildCount !== bChildCount) {
        return bChildCount - aChildCount;
      }

      // If same number of children, sort by total descendant count
      const aDescendantCount = a.descendants ? a.descendants().length : 1;
      const bDescendantCount = b.descendants ? b.descendants().length : 1;

      if (aDescendantCount !== bDescendantCount) {
        return bDescendantCount - aDescendantCount;
      }

      // Finally, sort by value
      return (b.value || 0) - (a.value || 0);
    }

    const processedData = preprocessData(JSON.parse(JSON.stringify(data)));

    // Create hierarchy with enhanced sorting
    const root = d3
      .hierarchy(processedData)
      .sum((d) => {
        // Use only leaf values to avoid double counting
        return d.children && d.children.length > 0 ? 0 : d.value || 0;
      })
      .sort(sortNodesByChildren);

    // Apply custom angle arrangement to put larger segments at top
    function arrangeNodesAtTop(node) {
      if (!node.children || node.children.length === 0) return;

      // Sort children by their total value and child count
      node.children.sort((a, b) => {
        const aChildCount = (a.children && a.children.length) || 0;
        const bChildCount = (b.children && b.children.length) || 0;

        // Prioritize nodes with more children
        if (aChildCount !== bChildCount) {
          return bChildCount - aChildCount;
        }

        // Then by value
        return b.value - a.value;
      });

      // Recursively arrange children
      node.children.forEach(arrangeNodesAtTop);
    }

    // Arrange nodes before partitioning
    arrangeNodesAtTop(root);

    // Create partition with better spacing
    const partition = d3
      .partition()
      .size([2 * Math.PI, root.height + 1])
      .padding(0.002);

    partition(root);

    // Custom angle adjustment to start largest segments at top (12 o'clock)
    function adjustAnglesForTopPlacement(node, offset = 0) {
      if (node.depth === 0) {
        // For root level, calculate total span and center it at top
        const totalSpan = node.children
          ? node.children.reduce((sum, child) => sum + (child.x1 - child.x0), 0)
          : 0;

        // Start angle to center the largest segments at the top
        const startOffset = -totalSpan / 2;

        if (node.children) {
          let currentAngle = startOffset;
          node.children.forEach((child) => {
            const span = child.x1 - child.x0;
            child.x0 = currentAngle;
            child.x1 = currentAngle + span;
            currentAngle += span;
            adjustAnglesForTopPlacement(child);
          });
        }
      } else {
        // For deeper levels, maintain relative positioning
        if (node.children) {
          const nodeSpan = node.x1 - node.x0;
          const childrenTotalSpan = node.children.reduce(
            (sum, child) => sum + (child.x1 - child.x0),
            0
          );

          let currentAngle = node.x0;
          node.children.forEach((child) => {
            const childSpan = child.x1 - child.x0;
            const adjustedSpan = (childSpan / childrenTotalSpan) * nodeSpan;
            child.x0 = currentAngle;
            child.x1 = currentAngle + adjustedSpan;
            currentAngle += adjustedSpan;
            adjustAnglesForTopPlacement(child);
          });
        }
      }
    }

    // Apply the angle adjustment
    adjustAnglesForTopPlacement(root);

    // Get all nodes including parents (depth > 0 to exclude root)
    const nodes = root.descendants().filter((d) => d.depth > 0);
    nodes.forEach((d, i) => {
      d.uid = i;
      d.current = d;
      // Store original positions for reference
      d.original = {
        x0: d.x0,
        x1: d.x1,
        y0: d.y0,
        y1: d.y1,
      };
    });
    let currentFocus = root;

    // Enhanced scales with better ring separation
    const yScale = d3
      .scaleLinear()
      .domain([0, root.height + 1])
      .range([radius * 0.08, radius]);

    // Improved arc with better padding
    const arc = d3
      .arc()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius((d) => yScale(d.y0))
      .outerRadius((d) => yScale(d.y1))
      .padAngle((d) => {
        // Dynamic padding based on segment size
        const span = d.x1 - d.x0;
        return Math.min(0.015, span * 0.1);
      })
      .padRadius(radius * 0.1)
      .cornerRadius(1);

    // SVG
    const svg = d3
      .select(svgEl)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [
        -radius - margin,
        -radius - margin,
        (radius + margin) * 2,
        (radius + margin) * 2,
      ])
      .style("background-color", "transparent")
      .style("overflow", "visible");

    const baseColors = { 2: "#4e79a7", 1: "#9c27b0" };

    const colorFor = (d) => {
      const base = baseColors[d.data.type] || "#cccccc";
      const lightenTo = theme === "dark" ? bg : "#ffffff";
      const t = d.depth / (root.height + 1);
      return d3.interpolateRgb(base, lightenTo)(Math.min(t, 0.8));
    };

    // Reset button
    const resetGroup = svg.append("g").style("display", "none");
    resetGroup
      .append("circle")
      .attr("r", radius * 0.1)
      .attr("fill", "transparent")
      .attr("stroke", strokeClr)
      .attr("stroke-width", 1.5)
      .style("cursor", "pointer");
    resetGroup
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .text("↻")
      .style("pointer-events", "none")
      .style("fill", textClr)
      .style("font-size", `${radius * 0.15}px`);

    // Improved label thresholds - now dynamic based on current view
    const minSpanThreshold = 2 * Math.PI * 0.008;
    const minRingWidthThreshold = 0.08;
    const fontSize = radius * 0.032;

    function shouldShowLabel(d, focus) {
      if (d.depth === 0) return false;
      if (d.current.y1 <= d.current.y0) return false;
      if (focus !== root && !d.ancestors().includes(focus)) return false;

      const currentSpan = d.current.x1 - d.current.x0;
      const currentRingWidth = d.current.y1 - d.current.y0;
      const actualInnerRadius = yScale(d.current.y0);
      const actualOuterRadius = yScale(d.current.y1);
      const actualRingWidth = actualOuterRadius - actualInnerRadius;
      const actualArcLength = currentSpan * actualInnerRadius;
      const minArcLength = fontSize * 3;
      const minRingHeight = fontSize * 1.2;

      return (
        currentSpan > minSpanThreshold &&
        currentRingWidth > minRingWidthThreshold &&
        actualArcLength > minArcLength &&
        actualRingWidth > minRingHeight
      );
    }

    function breakTextIntoLines(text, maxCharsPerLine = 12) {
      if (!text) return [""];

      const words = text.split(/[\s-]+/);
      const lines = [];
      let currentLine = "";

      for (const word of words) {
        if (lines.length >= 2) break;

        const testLine = currentLine + (currentLine ? " " : "") + word;
        if (testLine.length <= maxCharsPerLine) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            currentLine =
              word.length > maxCharsPerLine
                ? word.slice(0, maxCharsPerLine - 1) + "…"
                : word;
          } else {
            lines.push(word.slice(0, maxCharsPerLine - 1) + "…");
            break;
          }
        }
      }

      if (currentLine && lines.length < 2) {
        lines.push(currentLine);
      }

      return lines.length > 0 ? lines : [text.slice(0, maxCharsPerLine)];
    }

    function calculateOptimalLabelPosition(d, focus = root) {
      const [x0, y0] = arc.centroid(d.current);
      const nudge = 1.01;
      const x = x0 * nudge;
      const y = y0 * nudge;

      return { x, y };
    }

    function clicked(event, p) {
      event.stopPropagation();
      currentFocus = p;
      const isRoot = p === root;
      const span = p.x1 - p.x0;
      const depth = p.depth;

      // Hide tooltip when clicking
      hideTooltip(true);

      requestAnimationFrame(() => {
        nodes.forEach((d) => {
          d.target = isRoot
            ? {
                x0: d.original.x0,
                x1: d.original.x1,
                y0: d.original.y0,
                y1: d.original.y1,
              }
            : {
                x0: Math.max(
                  0,
                  Math.min(2 * Math.PI, ((d.x0 - p.x0) / span) * 2 * Math.PI)
                ),
                x1: Math.max(
                  0,
                  Math.min(2 * Math.PI, ((d.x1 - p.x0) / span) * 2 * Math.PI)
                ),
                y0: Math.max(0, d.y0 - depth),
                y1: Math.max(0, d.y1 - depth),
              };
        });

        const transition = svg
          .transition()
          .duration(600)
          .ease(d3.easeQuadInOut);

        path
          .transition(transition)
          .attrTween("d", (d) => {
            const i = d3.interpolate(d.current, d.target);
            return (t) => {
              d.current = i(t);
              return arc(d.current);
            };
          })
          .on("end", function(d) {
            updateLabels(null, currentFocus);
          });

        updateLabels(transition, p);
        resetGroup
          .style("display", isRoot ? "none" : null)
          .on("click", isRoot ? null : (e) => clicked(e, root));
      });
    }

    // Enhanced tooltip content with button
    function createTooltipContent(d) {
      const isCategory = d.children && d.children.length > 0;
      let content = `${isCategory ? "Category" : "Topic"}: ${d.data.name}`;
      if (!isCategory && d.parent && d.parent.depth > 0 && d.parent.data?.name) {
        content += `\nCategory: ${d.parent.data.name}`;
      }
      content += `\nCount: ${d.data.count || 0}`;

      if (d.children && d.children.length > 0) {
        content += `\nSubtopics: ${d.children.length}`;
      }

      if (d.data.top_words) {
        const formattedWords = d.data.top_words
          .split(/[,;]/)
          .map((word) => word.trim())
          .filter((word) => word.length > 0)
          .slice(0, 8)
          .join(", ");
        content += `\n\nTop Words:\n${formattedWords}`;
      }

      return content;
    }

    // Draw paths with enhanced styling
    const path = svg
      .append("g")
      .selectAll("path")
      .data(nodes, (d) => d.uid)
      .join("path")
      .attr("d", (d) => arc(d.current))
      .attr("fill", colorFor)
      .attr("stroke", theme === "dark" ? "#34495e" : "#ecf0f1")
      .attr("stroke-width", (d) => {
        return d.children && d.children.length > 0 ? 2 : 1.5;
      })
      .style("cursor", (d) => (d.children ? "pointer" : "default"))
      .on("mouseenter", function(event, d) {
        d3.select(this)
          .raise()
          .style("filter", "drop-shadow(0 0 8px rgba(0,0,0,0.2))");

        // Clear previous tooltip content
        tooltip.html("");

        // Create content container
        const contentDiv = tooltip.append("div").style("margin-bottom", "10px");

        contentDiv.text(createTooltipContent(d));

        // Add deep dive button only if id > 0
        if (d.data.id > 0) {
          const buttonDiv = tooltip
            .append("div")
            .style("text-align", "center")
            .style("margin-top", "8px");

          buttonDiv
            .append("button")
            .text("View Deep Dive")
            .style("background", theme === "dark" ? "#3498db" : "#2980b9")
            .style("color", "white")
            .style("border", "none")
            .style("padding", "6px 12px")
            .style("border-radius", "4px")
            .style("cursor", "pointer")
            .style("font-size", "12px")
            .style("font-weight", "500")
            .style("transition", "background-color 0.2s")
            .on("mouseover", function() {
              d3.select(this).style(
                "background",
                theme === "dark" ? "#2980b9" : "#1f4e79"
              );
            })
            .on("mouseout", function() {
              d3.select(this).style(
                "background",
                theme === "dark" ? "#3498db" : "#2980b9"
              );
            })
            .on("click", function(event) {
              event.stopPropagation();
              handleDeepDive(d.data);
              hideTooltip(true);
            });
        }

        // Improved positioning using getBoundingClientRect
        const svgRect = svgEl.getBoundingClientRect();
        const [centerX, centerY] = arc.centroid(d.current);
        const svgCenterX = svgRect.width / 2;
        const svgCenterY = svgRect.height / 2;

        // Calculate position relative to viewport
        let x = svgRect.left + centerX + svgCenterX + 15;
        let y = svgRect.top + centerY + svgCenterY - 10;

        // Ensure tooltip stays within viewport
        const tooltipWidth = 280; // max-width
        const tooltipHeight = 150; // estimated height

        if (x + tooltipWidth > window.innerWidth) {
          x = svgRect.left + centerX + svgCenterX - tooltipWidth - 15;
        }
        if (y + tooltipHeight > window.innerHeight) {
          y = svgRect.top + centerY + svgCenterY - tooltipHeight + 10;
        }

        // Ensure minimum margins
        x = Math.max(10, Math.min(x, window.innerWidth - tooltipWidth - 10));
        y = Math.max(10, Math.min(y, window.innerHeight - tooltipHeight - 10));

        tooltip.style("left", `${x}px`).style("top", `${y}px`);
        
        showTooltip();
      })
      .on("mouseleave", function(event) {
        d3.select(this)
          .style("filter", null)
          .style("stroke-width", (d) =>
            d.children && d.children.length > 0 ? 2 : 1.5
          );

        // Only hide if not moving to tooltip
        const relatedTarget = event.relatedTarget;
        if (!relatedTarget || !tooltipRef.current.contains(relatedTarget)) {
          hideTooltip();
        }
      })
      .on("click", (event, d) => d.children && clicked(event, d));

    // Add tooltip mouse events
    tooltipRef.current.addEventListener("mouseenter", () => {
      showTooltip();
    });

    tooltipRef.current.addEventListener("mouseleave", () => {
      hideTooltip();
    });

    // Enhanced labels - always upright
    const labelGroup = svg.append("g").attr("class", "labels");

    function updateLabels(transition = null, focus = root) {
      const visible = nodes.filter((d) => shouldShowLabel(d, focus));

      labelGroup.selectAll("g.label-group").remove();

      const labelGroups = labelGroup
        .selectAll("g.label-group")
        .data(visible, (d) => d.uid)
        .join("g")
        .attr("class", "label-group")
        .style("opacity", 0);

      labelGroups.each(function(d) {
        const group = d3.select(this);
        const lines = breakTextIntoLines(d.data.name);
        const lineHeight = fontSize * 1.1;
        const totalHeight = lines.length * lineHeight;
        const startY = -totalHeight / 2 + lineHeight / 2;

        lines.forEach((line, i) => {
          group
            .append("text")
            .attr("dy", startY + i * lineHeight)
            .style("fill", textClr)
            .style("font-size", `${fontSize}px`)
            .style("font-weight", "400")
            .style("pointer-events", "none")
            .style("text-anchor", "middle")
            .text(line);
        });
      });

      if (transition) {
        labelGroups
          .transition(transition)
          .style("opacity", 1)
          .attrTween("transform", (d) => (t) => {
            const curr = d3.interpolate(d.current, d.target)(t);
            const [x0, y0] = arc.centroid(curr);
            const nudge = 1.01;
            const x = x0 * nudge,
              y = y0 * nudge;
            return `translate(${x},${y})`;
          });
      } else {
        labelGroups.style("opacity", 1).attr("transform", (d) => {
          const position = calculateOptimalLabelPosition(d, focus);
          return `translate(${position.x},${position.y})`;
        });
      }
    }

    updateLabels();
    resetGroup.style("display", "none");

    function isInsideContainer(event) {
      if (!containerRef.current) return false;
      const rect = containerRef.current?.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
    
      return (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      );
    }
    
    const handleGlobalMouseMove = (event) => {
      if (!isInsideContainer(event)) {
        hideTooltip(true); // Hide immediately when cursor is outside
      }
    };
    
    document.addEventListener("mousemove", handleGlobalMouseMove);
    // Cleanup function
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      document.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [data, width, height, theme]);

  return (
    <ErrorBoundary>
      <div ref={containerRef} className={styles.chartWrapper}>
        <div ref={tooltipRef} className={styles.tooltip} />
        <svg ref={svgRef} />
      </div>
    </ErrorBoundary>
  );
};

export default CustomSunburstChart;